/**
 * Separation Reports — unified view of all 15 reports.
 *
 * Route: /hr/separation-reports
 *
 * Pick a report from the dropdown, set date range if needed, click Run.
 * Results render as a generic table — column keys come from the first
 * row. Pure read-only.
 *
 * Two Excel bulk-upload exports (PF Form 10, ESI Exit) also live here for
 * statutory filing. The buttons are also exposed on the Statutory Exit
 * Dashboard; this page is the one-stop for all reports.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Button, Card, DatePicker, Select, Space, Table, Typography, message,
} from 'antd'
import {
  DownloadOutlined, FileExcelOutlined, PlayCircleOutlined,
} from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'

import {
  separationReportsApi, downloadBlob,
} from '@/features/hr/separationReportsApi'

const { Title, Text, Paragraph } = Typography
const { RangePicker } = DatePicker

export default function SeparationReportsPage() {
  const [reportKey, setReportKey] = useState<string | undefined>('resignation_pending')
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ])
  const [runKey, setRunKey] = useState(0)

  const listQ = useQuery({
    queryKey: ['separation-reports-list'],
    queryFn: separationReportsApi.list,
  })

  const reports = listQ.data ?? []
  const currentReport = useMemo(
    () => reports.find((r) => r.key === reportKey),
    [reports, reportKey],
  )
  const needsDates = !!currentReport?.needs_date_range

  const reportQ = useQuery({
    enabled: !!reportKey && runKey > 0,
    queryKey: ['report-run', reportKey, runKey],
    queryFn: () => separationReportsApi.run(reportKey!, {
      from_date: needsDates && range ? range[0].format('YYYY-MM-DD') : undefined,
      to_date: needsDates && range ? range[1].format('YYYY-MM-DD') : undefined,
    }),
  })

  const handleRun = () => {
    if (!reportKey) return
    if (needsDates && (!range || range.length < 2)) {
      message.warning('Pick a date range first')
      return
    }
    setRunKey((k) => k + 1)
  }

  const rows = reportQ.data ?? []
  const columns = useMemo(() => {
    if (rows.length === 0) return []
    const first = rows[0]
    return Object.keys(first).map((k) => ({
      title: prettifyKey(k),
      dataIndex: k,
      key: k,
      ellipsis: true,
      render: (v: unknown) => renderCell(v),
    }))
  }, [rows])

  // ─── Excel exports ─────────────────────────────────────
  const handleExportEpfo = async () => {
    try {
      const res = await separationReportsApi.exportEpfoForm10('DATA_READY')
      downloadBlob(res, `EPFO_Form10_${dayjs().format('YYYY-MM-DD')}.xlsx`)
      message.success('EPFO Form 10 downloaded')
    } catch {
      message.error('Failed to export EPFO Form 10')
    }
  }
  const handleExportEsi = async () => {
    try {
      const res = await separationReportsApi.exportEsiExit('DATA_READY')
      downloadBlob(res, `ESI_Exit_${dayjs().format('YYYY-MM-DD')}.xlsx`)
      message.success('ESI Exit downloaded')
    } catch {
      message.error('Failed to export ESI Exit')
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Separation Reports</Title>
      <Text type="secondary">
        15 reports covering pipeline, attrition, dues, and statutory filings.
        Date-range reports default to current month.
      </Text>

      <Card size="small" style={{ marginTop: 16 }}>
        <Space wrap>
          <Text strong>Report:</Text>
          <Select
            style={{ width: 320 }}
            value={reportKey}
            onChange={setReportKey}
            loading={listQ.isLoading}
            options={reports.map((r) => ({
              value: r.key,
              label: r.label + (r.needs_date_range ? '  (date range)' : ''),
            }))}
            placeholder="Pick a report"
          />
          {needsDates && (
            <RangePicker
              value={range as never}
              onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
              allowClear={false}
            />
          )}
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={handleRun}
            loading={reportQ.isFetching}
          >
            Run
          </Button>
        </Space>
      </Card>

      <Card
        size="small"
        title="Statutory Bulk Exports"
        style={{ marginTop: 16 }}
        extra={<Text type="secondary">For EPFO + ESIC portal bulk-upload</Text>}
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Pulls DATA_READY statutory exit records and shapes them roughly to the portal&apos;s
          bulk-upload format. HR&apos;s IT person maps columns to the latest template before upload.
        </Paragraph>
        <Space>
          <Button icon={<FileExcelOutlined />} onClick={handleExportEpfo}>
            Export EPFO Form 10 (PF)
          </Button>
          <Button icon={<FileExcelOutlined />} onClick={handleExportEsi}>
            Export ESI Exit
          </Button>
        </Space>
      </Card>

      <Card
        size="small"
        title={
          <Space>
            {currentReport?.label ?? 'Report'}
            <Text type="secondary" style={{ fontWeight: 400 }}>
              {rows.length > 0 ? `· ${rows.length} rows` : ''}
            </Text>
          </Space>
        }
        extra={rows.length > 0 && (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => downloadCsv(rows, currentReport?.key || 'report')}
          >
            CSV
          </Button>
        )}
        style={{ marginTop: 16 }}
      >
        {runKey === 0 ? (
          <Text type="secondary">Pick a report and click Run.</Text>
        ) : (
          <Table
            size="small"
            rowKey={(_, idx) => String(idx)}
            loading={reportQ.isFetching}
            dataSource={rows}
            columns={columns}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 50, showSizeChanger: true }}
          />
        )}
      </Card>
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────

function prettifyKey(k: string): string {
  return k
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function renderCell(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object') return JSON.stringify(v)
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

function downloadCsv(rows: Record<string, unknown>[], name: string) {
  if (rows.length === 0) return
  const keys = Object.keys(rows[0])
  const escape = (v: unknown): string => {
    const s = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = [
    keys.join(','),
    ...rows.map((r) => keys.map((k) => escape(r[k])).join(',')),
  ].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${name}_${dayjs().format('YYYY-MM-DD')}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}
